import React, { useEffect, useState, useRef} from "react";
import { Birth, tzOffsetHours } from "../utils/calculatorApi";
import { searchCities } from "../components/calculators/cities";
import "./BirthInputLegacy.css";

interface Suggestion {
  formatted: string;
  lat: number;
  lon: number;
  timezone?: { name?: string };
}

/* Date, time and place, which is everything the chart engine needs. Place
   autocompletes through Geoapify, the same provider the profile form already
   uses, so there is one geocoder in the app rather than two. */

/* Meta's in-app browser (Instagram, Facebook, Threads) and iOS's specialised
   time picker do not get along: tapping an <input type="time"> can dismiss the
   webview outright, which sends the visitor back to the feed and loses the
   form. Reported from a live Instagram ad, where every visitor arrives inside
   that browser.
 *
 * Two <select>s instead. Selects use the ordinary iOS picker sheet, which is
 * one of the oldest and best-supported controls in any webview, and they carry
 * the same precision: an exact hour and an exact minute.
 */
/* Checked at render, not once at module load: a module-level constant is
   evaluated before the component ever mounts (and before a prerendered page
   hydrates), and it cannot be exercised by a test without loading a second copy
   of React. Cost is one regex per render. */
export function isInAppBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  return /(Instagram|FBAN|FBAV|FB_IAB|FBIOS|Line\/|Twitter|Snapchat)/i.test(
    navigator.userAgent || "",
  );
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => {
  const period = h < 12 ? "AM" : "PM";
  const twelve = h % 12 === 0 ? 12 : h % 12;
  return { value: String(h).padStart(2, "0"), label: `${twelve} ${period}` };
});
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, m) =>
  String(m).padStart(2, "0"),
);

/** Same "HH:MM" contract as the native control, so nothing downstream changes. */
const TimeOfBirth: React.FC<{
  value: string;
  onChange: (next: string) => void;
}> = ({ value, onChange }) => {
  /* The half-picked state lives HERE, not in the composed value.
     Deriving both selects from `value` looked tidier and made the control
     impossible to use: choosing an hour emitted "" (no minute yet), which fed
     straight back in and blanked the select. Neither half could ever be set. */
  const [hh, setHh] = useState(() => (value ? value.split(":")[0] : ""));
  const [mm, setMm] = useState(() => (value ? value.split(":")[1] : ""));

  useEffect(() => {
    // Stay in step when the parent clears or replaces the value.
    if (!value) return;
    const [h, m] = value.split(":");
    setHh(h);
    setMm(m);
  }, [value]);

  if (!isInAppBrowser()) {
    return (
      <input
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  /* Only a COMPLETE time is published: a half-set one would have the chart
     computed from a default the visitor never chose, and an hour out moves the
     ascendant. */
  const emit = (nextH: string, nextM: string) => {
    setHh(nextH);
    setMm(nextM);
    onChange(nextH && nextM ? `${nextH}:${nextM}` : "");
  };

  return (
    <div className="lbi-timepair">
      <select
        aria-label="Hour of birth"
        value={hh}
        onChange={(e) => emit(e.target.value, mm)}
      >
        <option value="">Hour</option>
        {HOUR_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <select
        aria-label="Minute of birth"
        value={mm}
        onChange={(e) => emit(hh, e.target.value)}
      >
        <option value="">Min</option>
        {MINUTE_OPTIONS.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
    </div>
  );
};

const BirthInput: React.FC<{
  /**
   * `timezone` is the IANA zone name ("Asia/Kolkata"), passed as a THIRD
   * argument so existing callers keep working untouched. Birth.tz is only the
   * numeric offset, which is all the calculators need, but turning a match into
   * real profiles needs the zone name too (user_birth_details.birth_timezone),
   * and it is lost by the time tzOffsetHours() has run.
   */
  onSubmit: (b: Birth, placeLabel: string, timezone?: string) => void;
  busy?: boolean;
  cta: string;
  busyLabel?: string;
  /** Commit as soon as date, time and place are all present, and hide the
      button. For flows that ask for two charts, where a Confirm per side is a
      step the visitor should not have to take. Off by default: every existing
      caller keeps its button. */
  autoCommit?: boolean;
  /** Change this to make autoCommit re-evaluate. Needed when a value the parent
      owns (the name) is part of what makes the commit valid. */
  recommitKey?: string | number;
}> = ({ onSubmit, busy, cta, busyLabel, autoCommit, recommitKey }) => {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [query, setQuery] = useState("");
  const [place, setPlace] = useState<Suggestion | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (place || query.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      const key = process.env.REACT_APP_GEOAPIFY_API_KEY;
      fetch(
        `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
          query
        )}&format=json&limit=5&apiKey=${key}`
      )
        .then((r) => r.json())
        .then((d) => {
          /* Geoapify answers as {results} with format=json and as GeoJSON
             {features} without it. Read both so a change at their end, or a
             stray param, cannot silently empty the dropdown. */
          if (d?.results?.length) return setSuggestions(d.results);
          if (d?.features?.length)
            return setSuggestions(
              d.features.map((f: any) => ({
                formatted: f.properties?.formatted,
                lat: f.properties?.lat,
                lon: f.properties?.lon,
                timezone: f.properties?.timezone,
              }))
            );
          setSuggestions(searchCities(query));
        })
        .catch(() => setSuggestions(searchCities(query)));
    }, 350);
    return () => clearTimeout(t);
  }, [query, place]);

  /* Auto-commit fires on the value, not on every render, so re-renders from a
     sibling panel cannot re-submit the same details. */
  const lastCommitted = useRef<string>("");
  /* Clearing the guard lets the same birth details commit again. Without this,
     filling date/time/place BEFORE the name commits once with an empty name,
     the parent rejects it, and then typing the name changes nothing: the
     signature is unchanged so nothing re-fires and the CTA never turns on. */
  useEffect(() => {
    lastCommitted.current = "";
  }, [recommitKey]);
  useEffect(() => {
    if (!autoCommit || busy) return;
    if (!date || !time || !place) return;
    const sig = `${date}|${time}|${place.lat}|${place.lon}`;
    if (lastCommitted.current === sig) return;
    lastCommitted.current = sig;
    setError("");
    onSubmit(
      {
        date,
        time,
        lat: place.lat,
        lon: place.lon,
        tz: tzOffsetHours(place.timezone?.name || "Asia/Kolkata", date),
      },
      place.formatted,
      place.timezone?.name,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCommit, busy, date, time, place, recommitKey]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!date || !time) {
      setError("Please give both a date and a time of birth.");
      return;
    }
    if (!place) {
      setError("Pick your birth place from the list so we get the coordinates.");
      return;
    }
    setError("");
    onSubmit(
      {
        date,
        time,
        lat: place.lat,
        lon: place.lon,
        tz: tzOffsetHours(place.timezone?.name || "Asia/Kolkata", date),
      },
      place.formatted,
      place.timezone?.name
    );
  };

  return (
    <form className="birthinput" onSubmit={submit}>
      <div className="lbi-row">
        <label className="lbi-field">
          <span>Date of birth</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
          />
        </label>
        <label className="lbi-field">
          <span>Time of birth</span>
          <TimeOfBirth value={time} onChange={setTime} />
        </label>
      </div>

      <label className="lbi-field lbi-place">
        <span>Place of birth</span>
        <input
          type="text"
          value={query}
          placeholder="Start typing a city"
          autoComplete="off"
          onChange={(e) => {
            setQuery(e.target.value);
            setPlace(null);
          }}
        />
        {suggestions.length > 0 ? (
          <ul className="lbi-suggestions">
            {suggestions.map((s) => (
              <li key={`${s.lat}-${s.lon}-${s.formatted}`}>
                <button
                  type="button"
                  onClick={() => {
                    setPlace(s);
                    setQuery(s.formatted);
                    setSuggestions([]);
                  }}
                >
                  {s.formatted}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </label>

      <p className="lbi-note">
        Birth time matters. An hour out can move the rising sign and the houses,
        which changes the answer. If you are unsure, use the closest time you
        know and treat the result as approximate.
      </p>
      {autoCommit ? null : (

      <button type="submit" className="lbi-submit" disabled={busy}>
        {busy ? busyLabel || "Reading your chart…" : cta}
      </button>
      )}
      {error ? <p className="lbi-error">{error}</p> : null}
    </form>
  );
};

export default BirthInput;
